/**
 * Shared transform utilities for schedule-appointment payloads.
 * Used by the client-side config builder (src/services/configs/schedule-appointment.config.ts)
 * and the server-side route handler (pages/api/v1/schedule-appointment.ts).
 */

// ─── Request / Response Types ──────────────────────────────────────────────────

export interface ScheduleAppointmentRequest {
  user_uuid: string;
  practice_id: number;
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string;
  gender_identity: string;
  preferred_pronouns: string;
  preferred_language: string;
  patient_email?: string;
  patient_phone_number: string;
  patient_phone_type: string;
  patient_preferred_contact: string;
  patient_voicemail_ok: boolean;
  legal_sex?: string;
  department_id: number;
  self_pay: boolean;
  state: string;
  insurance_package_id: number;
  payer_id?: number | string;
  member_id: string;
  insurance_policy_holder_first_name: string;
  insurance_policy_holder_last_name: string;
  insurance_policy_holder_dob: string;
  insurance_policy_holder_sex: string;
  relationship_to_insured: string;
  appointment_id: number[];
  appointment_type_id?: number;
  appointment_type_name?: string;
  appointment_modality: string;
  ok_to_leave_vm: boolean;
  consenttocall?: boolean;
  consenttotext?: boolean;
  appointment_datetime: string;
  user_timezone: string;
  provider_id: number;
  specialities: string;
  careformat: string;
  address1: string;
  address2?: string;
  city: string;
  zip: string;
  signup_as_minor?: number | boolean | string;
  question_id_1?: boolean;
  question_id_2?: boolean;
  question_id_3?: boolean;
  question_id_4?: boolean;
  question_id_5?: boolean;
  question_id_6?: boolean;
  med_mgmt_ind?: number;
}

// ─── Transform Utilities ───────────────────────────────────────────────────────

/**
 * Resolves the insurance_package_id for the upstream request.
 * When self_pay is true the package id is always 0; otherwise the raw value is used.
 */
export function resolveInsurancePackageId(
  selfPay: boolean | undefined,
  rawPackageId: number | undefined,
): number | undefined {
  return selfPay ? 0 : rawPackageId;
}

/**
 * Coerces appointment_type_id to a number.
 * Returns undefined when the input is null or undefined.
 */
export function normalizeAppointmentTypeId(id: number | null | undefined): number | undefined {
  if (id !== undefined && id !== null) {
    return Number(id);
  }
  return undefined;
}

/**
 * Parses an insurance package / selected-policy value (string or number) into
 * a finite number. Returns undefined for non-numeric or non-finite inputs.
 * Used by the component layer before building the schedule-appointment payload.
 */
export function parseInsurancePackageId(selectedPolicy: unknown): number | undefined {
  if (typeof selectedPolicy === "number") {
    return Number.isFinite(selectedPolicy) ? selectedPolicy : undefined;
  }
  if (typeof selectedPolicy === "string") {
    const parsed = Number(selectedPolicy);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * Sanitizes a payer_id value: accepts string or number, validates it is a
 * positive integer, and returns undefined for any invalid input.
 * Shared between the component layer and the server-side mapper.
 */
export function sanitizePayerId(payerId: number | string | null | undefined): number | undefined {
  if (payerId === undefined || payerId === null) {
    return undefined;
  }

  if (typeof payerId === "number") {
    return Number.isInteger(payerId) && payerId > 0 ? payerId : undefined;
  }

  if (typeof payerId === "string") {
    const trimmedPayerId = payerId.trim();

    if (!trimmedPayerId || !/^\d+$/.test(trimmedPayerId)) {
      return undefined;
    }

    const numericPayerId = Number(trimmedPayerId);
    return Number.isInteger(numericPayerId) && numericPayerId > 0 ? numericPayerId : undefined;
  }

  return undefined;
}
